import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { Search } from "lucide-react";

const TITLES = {
  clones: "Clones",
  seeds: "Seeds",
  all: "All Genetics",
};

const SEO = {
  clones: {
    title: "Shop Cannabis Clones | Dark Growers Genetics",
    desc: "Browse elite, lab-tested cannabis clones from Dark Growers Genetics. Rooted, vigorous cuttings of top-shelf strains, shipped free next-day in discreet packaging.",
    canonical: "https://darkgrowers.com/shop/clones",
  },
  seeds: {
    title: "Regular Cannabis Seeds — Big Nan Line | Dark Growers Genetics",
    desc: "Shop the 2026 Big Nan regular seed line from the Dark Growers Breeder Circle. Natural (non-feminized) genetics in packs of 6 or 12 with free next-day shipping.",
    canonical: "https://darkgrowers.com/shop/seeds",
  },
  all: {
    title: "Shop All Genetics — Clones & Seeds | Dark Growers Genetics",
    desc: "Explore the full Dark Growers Genetics catalog of premium cannabis clones and regular seeds. Lab-tested, discreetly shipped free next-day to your door.",
    canonical: "https://darkgrowers.com/shop",
  },
};

const INTRO = {
  clones:
    "Every Dark Growers clone is a hand-selected mother cut, lab-tested clean for HLVD, TMV, CMV, TSWV and INSV. We ship vigorous, well-rooted cuttings that are ready to transplant, so you start every grow from proven, elite genetics. From gassy OGs to dessert-sweet cookie and Zkittlez crosses, our clone library represents the strains our breeders trust most. Buy two or more clones and use code DARK25 for 25% off your entire order.",
  seeds:
    "Our 2026 Breeder Circle seed line is built around the legendary Big Nan male — every strain is a Big Nan cross bred and tested in-house for stability, vigor and true-to-type expression. These are regular (non-feminized) seeds, sold in packs of 6 or 12 with high germination rates. Whether you are hunting new phenotypes or preserving genetics, the Big Nan line gives breeders and growers a dependable foundation. All orders ship free next-day in discreet packaging.",
  all:
    "The complete Dark Growers Genetics catalog brings together our elite rooted clones and our Breeder Circle regular seeds in one place. Each clone is lab-tested clean and each seed pack is bred in-house for consistency and potency. Filter by indica, sativa or hybrid, search by strain name, and check out securely — every order ships free next-day with discreet packaging.",
};

export default function Shop() {
  const { category } = useParams();
  const cat = category || "all";
  const seo = SEO[cat] || SEO.all;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (cat !== "all") params.category = cat;
    if (typeFilter !== "all") params.strain_type = typeFilter;
    if (search) params.search = search;
    api.get("/products", { params }).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, [cat, typeFilter, search]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <Seo title={seo.title} description={seo.desc} canonical={seo.canonical} />
      <div className="mb-4">
        <p className="label-caps text-emerald-500 mb-3">Dark Growers Genetics</p>
        <h1 className="font-display text-5xl md:text-6xl text-slate-100">{TITLES[cat]}</h1>
        <p className="text-slate-400 text-sm mt-5 max-w-3xl leading-relaxed" data-testid="shop-intro">
          {INTRO[cat]}
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-white/10 pb-6 mb-10">
        <div className="flex items-center gap-2 flex-wrap">
          {["all", "indica", "sativa", "hybrid"].map((t) => (
            <button
              key={t}
              data-testid={`filter-${t}`}
              onClick={() => setTypeFilter(t)}
              className={`label-caps px-4 py-2 border transition-colors ${
                typeFilter === t
                  ? "border-emerald-500 text-emerald-400 bg-emerald-500/10"
                  : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearchParams(e.target.value ? { q: e.target.value } : {})}
            placeholder="Search strains..."
            className="w-full bg-[#0F0F0F] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 pl-10 pr-4 py-2.5 transition-colors"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-[#0F0F0F] animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <p className="text-slate-500 text-center py-24">No genetics match your filters.</p>
      ) : (
        <>
          <p className="text-slate-500 text-xs mb-6" data-testid="results-count">
            {products.length} {products.length === 1 ? "result" : "results"}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
